import { ref } from 'vue';
import type { FormStructure } from '../../../types/formTypeStructure';
import type { FormValidationResult } from '../types/formEngine.types';
import { normalizeFormData } from '../../../types/repeatRules';
import { showToast } from '../../../utils/toastHelpers';

export function useFormSubmission(
  formValues: Record<string, any>,
  form: FormStructure,
  validatePage: () => Promise<FormValidationResult>,
  onSubmit: (data: Record<string, any>) => void,
  submitMethod?: (data: Record<string, any>) => Promise<any>
) {
  const isValidating = ref(false);

  const handleSubmitWithValidation = async () => {
    isValidating.value = true;
    try {
      const result = await validatePage();
      
      // Si la validation globale indique que le formulaire est invalide, bloquer la soumission
      if (!result.isValid) {
      // Afficher les erreurs détaillées par champ
      if (result.invalidFields && result.invalidFields.length > 0) {
        result.invalidFields.forEach(field => {
          field.errors.forEach(error => {
            showToast(`${field.fieldLabel} : ${error}`, 'error');
          });
        });
        return;
      }
      
        // Afficher les erreurs pour les champs requis vides (seulement si allRequiredFieldsFilled est false)
        if (!result.allRequiredFieldsFilled && result.emptyRequiredFields && result.emptyRequiredFields.length > 0) {
        result.emptyRequiredFields.forEach(field => {
          showToast(`${field.fieldLabel} : ${field.errors[0]}`, 'error');
        });
        return;
      }
      
        // Afficher les erreurs pour les champs requis non touchés (seulement si allRequiredFieldsFilled est false)
        if (!result.allRequiredFieldsFilled && result.untouchedRequiredFields && result.untouchedRequiredFields.length > 0) {
        result.untouchedRequiredFields.forEach(field => {
          showToast(`${field.fieldLabel} : ${field.errors[0]}`, 'error');
        });
          return;
        }
        
        // Si isValid est false mais qu'on n'a pas d'erreurs spécifiques, afficher un message générique
        showToast('Veuillez remplir tous les champs requis', 'error');
        return;
      }
      
      // Préparer les données (compléter champs masqués)
      const { prepareFormDataBeforeSubmit, flattenOTPFields } = await import('../../../types/formUtils');
      const completedData = prepareFormDataBeforeSubmit(formValues, form.sections);
      const flattenedData = flattenOTPFields(completedData, form.sections);
      
      // Normaliser les données
      const normalizedData = normalizeFormData(flattenedData, form.sections);

      // Utiliser la méthode de soumission personnalisée si elle est fournie
      if (submitMethod) {
        showToast('📤 Envoi des données en cours...', 'info');
        
        try {
          const result = await submitMethod(normalizedData);
          console.log('✅ Soumission réussie:', result);
          
          // Appeler aussi la méthode onSubmit par défaut si elle existe
          if (onSubmit) {
            onSubmit(normalizedData);
          }
          
          showToast(`Formulaire soumis avec succès ! 🎉 (${result.requiredFieldsCount} champs requis traités)`, 'success');
        } catch (error) {
          console.error('❌ Erreur lors de la soumission:', error);
          showToast(`❌ Erreur lors de la soumission: ${error instanceof Error ? error.message : 'Erreur inconnue'}`, 'error');
          throw error;
        }
      } else {
        // Utiliser la méthode onSubmit par défaut
        onSubmit(normalizedData);
        showToast(`Formulaire soumis avec succès ! 🎉 (${result.requiredFieldsCount} champs requis traités)`, 'success');
      }
    } finally {
      isValidating.value = false;
    }
  };

  return {
    handleSubmitWithValidation,
    isValidating
  };
}

